import { API } from './interceptor'

const CATCH_BOARD_BASE = 'catch-board'

function throwCatchBoardError(response, fallbackMessage) {
  const message =
    response?.message ||
    response?.data?.message ||
    fallbackMessage ||
    'Catch Board request failed'
  const error = new Error(message)
  error.code = response?.code || response?.data?.code || 'CATCH_BOARD_ERROR'
  error.status = response?.status
  throw error
}

function assertCatchBoardSuccess(response, fallbackMessage) {
  if (!response?.success) {
    throwCatchBoardError(response, fallbackMessage)
  }
  return response.data
}

/**
 * Axios rejects non-2xx responses — pull Catch Board JSON off error.response.data
 * so the modal can show the real validation message.
 */
async function catchBoardRequest(request, fallbackMessage) {
  try {
    const response = await request()
    return assertCatchBoardSuccess(response, fallbackMessage)
  } catch (error) {
    if (error?.code && error?.message && !error?.response) {
      throw error
    }

    const payload = error?.response?.data
    if (payload && typeof payload === 'object') {
      throwCatchBoardError(
        {
          ...payload,
          status: error.response?.status || payload.status,
        },
        fallbackMessage,
      )
    }

    throwCatchBoardError(
      { message: error?.message, status: error?.response?.status },
      fallbackMessage,
    )
  }
}

/**
 * List Catch Board deals for the kanban.
 * @param {Record<string, string|number|boolean|undefined>} [queryParams]
 */
export async function fetchCatchBoardDeals(queryParams = {}) {
  return catchBoardRequest(
    () =>
      API.get('api2', `${CATCH_BOARD_BASE}/deals`, {
        queryStringParameters: queryParams,
      }),
    'Failed to load Catch Board deals',
  )
}

/**
 * @param {string} dealId
 */
export async function fetchCatchBoardDeal(dealId) {
  const data = await catchBoardRequest(
    () => API.get('api2', `${CATCH_BOARD_BASE}/deals/${dealId}`),
    'Failed to load Catch Board deal',
  )
  return data?.deal ?? null
}

/**
 * Lazy Spartacus / Crow's Nest context for a deal modal.
 * @param {string} dealId
 * @param {Record<string, string|number|undefined>} [queryParams]
 */
export async function fetchCatchBoardDealContext(dealId, queryParams = {}) {
  const data = await catchBoardRequest(
    () =>
      API.get('api2', `${CATCH_BOARD_BASE}/deals/${dealId}/context`, {
        queryStringParameters: queryParams,
      }),
    'Failed to load Catch Board deal context',
  )
  return data?.context ?? null
}

/**
 * Open deals that already share the given venue ids (warn-only duplicate check).
 * @param {{ venueIds?: string[], excludeDealId?: string|null }} [options]
 * @returns {Promise<Array<{ venueId: string, deals: Array<{ dealId: string, name: string, stage: string|null }> }>>}
 */
export async function fetchCatchBoardDuplicateVenueWarnings({
  venueIds = [],
  excludeDealId = null,
} = {}) {
  const ids = [
    ...new Set(
      (Array.isArray(venueIds) ? venueIds : [venueIds])
        .map(id => String(id || '').trim())
        .filter(Boolean),
    ),
  ]
  if (!ids.length) {
    return []
  }

  const queryStringParameters = {
    venueIds: ids.join(','),
  }
  if (excludeDealId) {
    queryStringParameters.excludeDealId = String(excludeDealId)
  }

  const data = await catchBoardRequest(
    () =>
      API.get('api2', `${CATCH_BOARD_BASE}/duplicate-venue-warnings`, {
        queryStringParameters,
      }),
    'Failed to check Catch Board venue duplicates',
  )

  const warnings = data?.duplicateVenueWarnings
  return Array.isArray(warnings)
    ? warnings.map(warning => ({
        venueId: warning?.venueId ? String(warning.venueId) : null,
        deals: Array.isArray(warning?.deals)
          ? warning.deals.map(deal => ({
              dealId: deal?.dealId ? String(deal.dealId) : null,
              name: deal?.name || '',
              stage: deal?.stage || null,
            }))
          : [],
      }))
    : []
}

/**
 * OMS admins available as Catch Board deal owners.
 * @returns {Promise<Array<{ accountId: string, name: string, email: string }>>}
 */
export async function fetchCatchBoardAssignees() {
  const data = await catchBoardRequest(
    () => API.get('api2', `${CATCH_BOARD_BASE}/assignees`),
    'Failed to load Catch Board assignees',
  )
  const assignees = data?.assignees
  return Array.isArray(assignees)
    ? assignees.map(row => ({
        accountId: row?.accountId ? String(row.accountId) : '',
        name: row?.name || '',
        email: row?.email || '',
      }))
    : []
}

/**
 * @param {Record<string, unknown>} body
 */
export async function createCatchBoardDeal(body = {}) {
  const data = await catchBoardRequest(
    () =>
      API.post('api2', `${CATCH_BOARD_BASE}/deals`, {
        body,
      }),
    'Failed to create Catch Board deal',
  )
  return data?.deal ?? null
}

/**
 * @param {string} dealId
 * @param {Record<string, unknown>} body
 */
export async function updateCatchBoardDeal(dealId, body = {}) {
  const data = await catchBoardRequest(
    () =>
      API.patch('api2', `${CATCH_BOARD_BASE}/deals/${dealId}`, {
        body,
      }),
    'Failed to update Catch Board deal',
  )
  return data?.deal ?? null
}

/**
 * Move / reorder a deal within or across columns.
 * @param {string} dealId
 * @param {{ stage: string, position: number, wonPlan?: string, wonMrrPence?: number, wonGoLiveDate?: string, lostReason?: string, lostReasonNote?: string }} body
 */
export async function moveCatchBoardDealStage(dealId, body = {}) {
  const data = await catchBoardRequest(
    () =>
      API.patch('api2', `${CATCH_BOARD_BASE}/deals/${dealId}/stage`, {
        body,
      }),
    'Failed to move Catch Board deal',
  )
  return data?.deal ?? null
}

/**
 * Soft-delete a deal.
 * @param {string} dealId
 */
export async function deleteCatchBoardDeal(dealId) {
  const data = await catchBoardRequest(
    () => API.del('api2', `${CATCH_BOARD_BASE}/deals/${dealId}`),
    'Failed to delete Catch Board deal',
  )
  return data?.deal ?? null
}
